import { useState } from 'react'
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom'
import { cn } from '@/shared/lib/utils'
import { useAuthStore } from '@/core/stores/auth'
import { TremorLogo } from '@/shared/components/ui/TremorLogo'
import { LogOut, ChevronLeft, Menu } from 'lucide-react'
import { VerificationBanner } from '@/core/components/VerificationBanner'
import api from '@/shared/lib/api'

// ── Inline SVG Icon Components ──────────────────────────────────────
function IconAccueil() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <rect x="4" y="4" width="17" height="17" rx="2.5" fill="rgba(201,168,76,.12)" stroke="#C9A84C" strokeWidth="1.5"/>
      <rect x="23" y="4" width="17" height="17" rx="2.5" fill="none" stroke="rgba(245,243,239,.22)" strokeWidth="1.2"/>
      <rect x="4" y="23" width="17" height="17" rx="2.5" fill="none" stroke="rgba(245,243,239,.16)" strokeWidth="1.1"/>
      <rect x="23" y="23" width="17" height="17" rx="2.5" fill="none" stroke="rgba(245,243,239,.1)" strokeWidth=".9"/>
      <rect x="8" y="14" width="3" height="5" rx=".8" fill="rgba(201,168,76,.7)"/>
      <rect x="12.5" y="10" width="3" height="9" rx=".8" fill="#C9A84C"/>
      <rect x="17" y="12" width="3" height="7" rx=".8" fill="rgba(201,168,76,.55)"/>
    </svg>
  )
}

function IconLLR() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <circle cx="22" cy="28" r="7" stroke="rgba(201,168,76,.2)" strokeWidth="1.2" strokeDasharray="2 2.5"/>
      <circle cx="22" cy="28" r="13" stroke="rgba(201,168,76,.1)" strokeWidth="1" strokeDasharray="2 3"/>
      <path d="M22 8C17.6 8 14 11.6 14 16C14 22 22 30 22 30C22 30 30 22 30 16C30 11.6 26.4 8 22 8Z"
        stroke="#C9A84C" strokeWidth="1.5" fill="rgba(201,168,76,.1)" strokeLinejoin="round"/>
      <circle cx="22" cy="16" r="3.5" stroke="#C9A84C" strokeWidth="1.2" fill="none"/>
      <polyline points="18,16 19.5,16 20.5,12.5 22,20 23.5,16 25.5,16"
        stroke="#C9A84C" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  )
}

function IconGFO() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <path d="M6 22C6 22 12 12 22 12C32 12 38 22 38 22C38 22 32 32 22 32C12 32 6 22 6 22Z"
        stroke="#7B61FF" strokeWidth="1.5" fill="rgba(123,97,255,.07)" strokeLinejoin="round"/>
      <circle cx="22" cy="22" r="6.5" stroke="#7B61FF" strokeWidth="1.3" fill="rgba(123,97,255,.12)"/>
      <circle cx="22" cy="22" r="2.5" fill="#7B61FF"/>
      <line x1="22" y1="13" x2="22" y2="16.5" stroke="rgba(123,97,255,.45)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="22" y1="27.5" x2="22" y2="31" stroke="rgba(123,97,255,.45)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="13" y1="22" x2="16.5" y2="22" stroke="rgba(123,97,255,.45)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="27.5" y1="22" x2="31" y2="22" stroke="rgba(123,97,255,.45)" strokeWidth="1" strokeLinecap="round"/>
      <circle cx="25" cy="19.5" r="1.2" fill="rgba(255,255,255,.22)"/>
    </svg>
  )
}

function IconARE() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <path d="M22 8A14 14 0 0 1 36 22" stroke="#E8562A" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <path d="M36 22A14 14 0 0 1 22 36" stroke="rgba(232,86,42,.55)" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
      <path d="M22 36A14 14 0 0 1 8 22" stroke="rgba(232,86,42,.32)" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
      <path d="M8 22A14 14 0 0 1 22 8" stroke="rgba(232,86,42,.16)" strokeWidth="1" strokeLinecap="round" fill="none"/>
      <path d="M33.5 17L36 22L31 21.5" stroke="#E8562A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M22 15L23.4 19.3H27.9L24.3 21.8L25.6 26.1L22 23.5L18.4 26.1L19.7 21.8L16.1 19.3H20.6L22 15Z"
        fill="#E8562A" opacity=".85"/>
    </svg>
  )
}

function IconSDI() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <path d="M16 8H28V18L34 32C34 35 31.5 36 28 36H16C12.5 36 10 35 10 32L16 18V8Z"
        stroke="#0F7173" strokeWidth="1.4" fill="none" strokeLinejoin="round"/>
      <path d="M11.5 29.5C11.5 29.5 15.5 26.5 22 27C28.5 27.5 32.5 29.5 32.5 29.5L34 32C34 35 31.5 36 28 36H16C12.5 36 10 35 10 32L11.5 29.5Z"
        fill="rgba(15,113,115,.2)"/>
      <line x1="28" y1="22" x2="31" y2="22" stroke="rgba(15,113,115,.4)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="28" y1="26" x2="32" y2="26" stroke="rgba(15,113,115,.3)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="28" y1="30" x2="31" y2="30" stroke="rgba(15,113,115,.4)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="16" y1="10" x2="28" y2="10" stroke="rgba(15,113,115,.22)" strokeWidth=".9" strokeLinecap="round"/>
      <circle cx="22" cy="28.5" r="2.5" fill="#0F7173"/>
    </svg>
  )
}

function IconEquipe() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <line x1="22" y1="10" x2="10" y2="28" stroke="rgba(245,243,239,.16)" strokeWidth="1.2"/>
      <line x1="22" y1="10" x2="34" y2="28" stroke="rgba(245,243,239,.16)" strokeWidth="1.2"/>
      <line x1="10" y1="28" x2="34" y2="28" stroke="rgba(245,243,239,.12)" strokeWidth="1.2"/>
      <line x1="22" y1="10" x2="22" y2="36" stroke="rgba(245,243,239,.07)" strokeWidth="1"/>
      <circle cx="22" cy="10" r="3.5" fill="#1A1816" stroke="rgba(245,243,239,.65)" strokeWidth="1.3"/>
      <circle cx="22" cy="10" r="6" stroke="rgba(245,243,239,.07)" strokeWidth="1" fill="none"/>
      <circle cx="10" cy="28" r="3" fill="#1A1816" stroke="rgba(245,243,239,.5)" strokeWidth="1.2"/>
      <circle cx="34" cy="28" r="3" fill="#1A1816" stroke="rgba(245,243,239,.5)" strokeWidth="1.2"/>
      <circle cx="22" cy="36" r="2.2" fill="#1A1816" stroke="rgba(245,243,239,.28)" strokeWidth="1"/>
    </svg>
  )
}

function IconSettings() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <circle cx="22" cy="22" r="14" stroke="rgba(245,243,239,.25)" strokeWidth="1.3" fill="none"/>
      <line x1="22" y1="8.5" x2="22" y2="12" stroke="rgba(245,243,239,.45)" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="22" y1="32" x2="22" y2="35.5" stroke="rgba(245,243,239,.2)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="8.5" y1="22" x2="12" y2="22" stroke="rgba(245,243,239,.2)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="32" y1="22" x2="35.5" y2="22" stroke="rgba(245,243,239,.2)" strokeWidth="1" strokeLinecap="round"/>
      <line x1="12.5" y1="12.5" x2="14.6" y2="14.6" stroke="rgba(245,243,239,.15)" strokeWidth=".9" strokeLinecap="round"/>
      <line x1="29.4" y1="29.4" x2="31.5" y2="31.5" stroke="rgba(245,243,239,.15)" strokeWidth=".9" strokeLinecap="round"/>
      <line x1="31.5" y1="12.5" x2="29.4" y2="14.6" stroke="rgba(245,243,239,.15)" strokeWidth=".9" strokeLinecap="round"/>
      <line x1="14.6" y1="29.4" x2="12.5" y2="31.5" stroke="rgba(245,243,239,.15)" strokeWidth=".9" strokeLinecap="round"/>
      <line x1="22" y1="22" x2="28" y2="12" stroke="rgba(245,243,239,.75)" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="22" cy="22" r="2.5" fill="rgba(245,243,239,.7)"/>
      <circle cx="22" cy="22" r="1" fill="#1A1816"/>
    </svg>
  )
}

function IconWiki() {
  return (
    <svg width="20" height="20" viewBox="0 0 44 44" fill="none" className="flex-shrink-0">
      <path d="M8 4H24L30 10V32H8V4Z"
        stroke="rgba(245,243,239,.38)" strokeWidth="1.3" fill="rgba(245,243,239,.04)" strokeLinejoin="round"/>
      <path d="M24 4V10H30" stroke="rgba(245,243,239,.2)" strokeWidth="1" strokeLinecap="round"/>
      <path d="M21 12L13.5 22H20L15 31"
        stroke="rgba(245,243,239,.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  )
}

// ── Types ────────────────────────────────────────────────────────────
type NavItem = { to: string; label: string; icon: React.ElementType; adminOnly?: boolean }
type Module = {
  id: string; label: string; shortLabel: string; fullName: string; icon: React.ElementType
  color: string; basePath: string; nav: NavItem[]
}

// ── Module & Nav config ─────────────────────────────────────────────
const modules: Module[] = [
  {
    id: 'llr', label: 'Nova', shortLabel: 'Nova', fullName: 'Local Lead Radar',
    icon: IconLLR, color: '#C9A84C', basePath: '/llr',
    nav: [
      { to: '/llr', label: 'Radar Hub', icon: IconLLR },
      { to: '/llr/alerts', label: 'Alertes', icon: IconLLR },
      { to: '/llr/radars/new', label: 'Nouveau Radar', icon: IconLLR },
    ],
  },
  {
    id: 'gfo', label: 'Vega', shortLabel: 'Vega', fullName: 'Google Footprint Optimizer',
    icon: IconGFO, color: '#7B61FF', basePath: '/gfo',
    nav: [
      { to: '/gfo', label: 'Dashboard', icon: IconGFO },
      { to: '/gfo/keywords', label: 'Mots-clés', icon: IconGFO },
      { to: '/gfo/reviews', label: 'Avis Google', icon: IconGFO },
      { to: '/gfo/opportunities', label: 'Opportunités', icon: IconGFO },
    ],
  },
  {
    id: 'are', label: 'Halo', shortLabel: 'Halo', fullName: 'Auto Reputation Engine',
    icon: IconARE, color: '#E8562A', basePath: '/are',
    nav: [
      { to: '/are', label: 'Dashboard', icon: IconARE },
      { to: '/are/contacts', label: 'Contacts', icon: IconARE },
      { to: '/are/activity', label: 'Activité', icon: IconARE },
      { to: '/are/feedback', label: 'Feedback', icon: IconARE },
      { to: '/are/analytics', label: 'Analytics', icon: IconARE },
      { to: '/are/campaigns/new', label: 'Campagne', icon: IconARE },
    ],
  },
  {
    id: 'sdi', label: 'Apex', shortLabel: 'Apex', fullName: 'Search Demand Intelligence',
    icon: IconSDI, color: '#0F7173', basePath: '/sdi',
    nav: [
      { to: '/sdi', label: 'Dashboard', icon: IconSDI },
      { to: '/sdi/markets', label: 'Marchés', icon: IconSDI },
    ],
  },
]

const sharedNav: NavItem[] = [
  { to: '/settings/team', label: 'Équipe', icon: IconEquipe, adminOnly: true },
  { to: '/settings/connectors', label: 'Connecteurs', icon: IconSettings, adminOnly: true },
  { to: '/settings', label: 'Paramètres', icon: IconSettings },
]

function detectModule(pathname: string): Module | null {
  if (pathname.startsWith('/gfo')) return modules[1]
  if (pathname.startsWith('/are')) return modules[2]
  if (pathname.startsWith('/sdi')) return modules[3]
  if (pathname.startsWith('/llr')) return modules[0]
  return null
}

/** Vertical glow bar — faded at extremities like LED backlight */
function GlowBar({ color, active }: { color: string; active: boolean }) {
  if (!active) return <div className="w-[3px] flex-shrink-0" />
  return (
    <div className="w-[3px] self-stretch flex-shrink-0 rounded-full overflow-hidden">
      <div className="w-full h-full rounded-full"
        style={{
          background: `linear-gradient(180deg, transparent 0%, ${color} 25%, ${color} 75%, transparent 100%)`,
          boxShadow: `0 0 8px ${color}60, 0 0 16px ${color}30`,
        }} />
    </div>
  )
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  const activeModule = detectModule(location.pathname)
  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  const isHome = location.pathname === '/' || location.pathname === '/home'

  const handleLogout = () => { logout(); navigate('/login') }

  return (
    <div className="flex h-screen overflow-hidden bg-paper">
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-onyx/40 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* ═══ SIDEBAR ═══ */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-onyx text-paper border-r border-onyx-border shadow-sidebar transition-all duration-300 lg:relative',
        collapsed ? 'w-[72px]' : 'w-[260px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      )}>
        {/* ── Logo — link to Home ── */}
        <Link to="/home" className="block bg-onyx-dark border-b border-onyx-border px-5 py-5 hover:bg-onyx-light/30 transition-colors">
          {collapsed ? (
            <div className="flex justify-center">
              <TremorLogo size={24} className="text-gold" />
            </div>
          ) : (
            <div className="flex items-center gap-3 animate-fade-in">
              <TremorLogo size={30} className="text-gold" />
              <div>
                <p className="font-serif text-[22px] font-bold text-paper tracking-tight leading-none">
                  tremor<em className="not-italic font-light text-gold">app</em>
                </p>
                <p className="text-[8px] tracking-[0.22em] uppercase text-gold/45 mt-1">
                  {user?.full_name ? `${user.full_name.split(' ')[0]}'s workspace` : 'Intelligence Marché'}
                </p>
              </div>
            </div>
          )}
        </Link>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto py-2">
          {/* Accueil */}
          <NavLink to="/home" onClick={() => setMobileOpen(false)}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors mx-2 rounded-lg mb-1',
              isActive || isHome ? 'bg-onyx-light text-paper' : 'text-paper/50 hover:bg-onyx-light/50 hover:text-paper/80',
              collapsed && 'justify-center px-0 mx-0 rounded-none',
            )}>
            <IconAccueil />
            {!collapsed && <span>Accueil</span>}
          </NavLink>

          {/* Module label */}
          {!collapsed && <p className="text-[9px] tracking-[0.2em] uppercase text-gold/40 px-4 pt-4 pb-2">Modules</p>}
          {collapsed && <div className="border-t border-onyx-border mx-2 my-2" />}

          {/* Modules */}
          {modules.map((mod) => {
            const isActive = activeModule?.id === mod.id
            return (
              <div key={mod.id}>
                <NavLink to={mod.basePath} end onClick={() => setMobileOpen(false)}
                  className={() => cn(
                    'relative flex items-center gap-2.5 py-2.5 text-sm font-medium transition-colors',
                    isActive ? 'text-paper' : 'text-paper/50 hover:text-paper/80',
                    collapsed ? 'justify-center px-2' : 'px-2',
                  )}>
                  <GlowBar color={mod.color} active={isActive} />
                  <div className={cn(
                    'flex items-center gap-2.5 flex-1 min-w-0 py-0.5 px-2 rounded-md transition-colors',
                    isActive && 'bg-onyx-light',
                    !isActive && 'hover:bg-onyx-light/40',
                  )}>
                    <mod.icon />
                    {!collapsed && <span className="truncate">{mod.label}</span>}
                  </div>
                </NavLink>

                {isActive && !collapsed && (
                  <div className="ml-[18px] pl-4 border-l border-onyx-border/50 animate-fade-in mb-1">
                    {mod.nav.map((item) => (
                      <NavLink key={item.to} to={item.to} end={item.to === mod.basePath}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive: sub }) => cn(
                          'block py-1.5 text-[12px] transition-colors',
                          sub ? 'text-gold font-medium' : 'text-paper/40 hover:text-paper/70',
                        )}>
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          {/* Shared nav */}
          {!collapsed && <div className="divider-gold mx-4 my-3" />}
          {collapsed && <div className="border-t border-onyx-border mx-2 my-2" />}
          {sharedNav.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors mx-2 rounded-lg',
                isActive ? 'text-paper bg-onyx-light' : 'text-paper/50 hover:text-paper/80 hover:bg-onyx-light/40',
                collapsed && 'justify-center px-0 mx-0 rounded-none',
              )}>
              <item.icon />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* ── Wiki link — above footer ── */}
        <div className="border-t border-onyx-border px-2 py-2">
          <NavLink to="/wiki" onClick={() => setMobileOpen(false)}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-colors rounded-lg',
              isActive ? 'text-gold bg-onyx-light' : 'text-paper/40 hover:text-paper/60 hover:bg-onyx-light/30',
              collapsed && 'justify-center px-0 rounded-none',
            )}>
            <IconWiki />
            {!collapsed && <span>TremorWiki</span>}
          </NavLink>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-onyx-border p-3 space-y-2">
          {!collapsed && user && (
            <div className="flex items-center gap-3 px-2 py-2 animate-fade-in">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gold text-onyx text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {(user.full_name || user.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-paper truncate">{user.full_name || user.email}</p>
                <p className="text-[11px] text-paper/50 truncate">{user.role}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <button onClick={handleLogout} title="Déconnexion"
              className={cn('flex items-center gap-2 px-3 py-2 text-sm text-paper/50 hover:text-brick transition-colors flex-1 rounded-none', collapsed && 'justify-center')}>
              <LogOut size={16} />
              {!collapsed && <span>Déconnexion</span>}
            </button>
            <button onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex items-center justify-center h-9 w-9 text-paper/50 hover:text-gold transition-colors">
              <ChevronLeft size={16} className={cn('transition-transform', collapsed && 'rotate-180')} />
            </button>
          </div>
        </div>
      </aside>

      {/* ═══ MAIN AREA (banner + content) ═══ */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Email verification banner */}
        {user && !user.email_verified && (
          <VerificationBanner
            email={user.email}
            onResend={async () => {
              await api.post('/auth/resend-verification', { email: user.email })
            }}
          />
        )}

        <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-paper">
          <div className="flex h-14 items-center gap-3 border-b border-ink-border bg-paper px-4 lg:hidden">
            <button onClick={() => setMobileOpen(true)}><Menu size={22} className="text-ink" /></button>
            <Link to="/home" className="flex items-center gap-2">
              <TremorLogo size={20} className="text-gold" />
              <span className="font-serif text-base font-bold text-ink tracking-tight">
                tremor<em className="not-italic font-light text-gold">app</em>
              </span>
            </Link>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 animate-fade-in">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
